import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell, Area, AreaChart, ReferenceLine } from 'recharts';
import { Clock, CreditCard, Banknote, Receipt, TrendingUp, ArrowUpRight, Flame, AlertTriangle, CheckCircle, Target } from 'lucide-react';
import UpcomingPayments from '../components/UpcomingPayments';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard = () => {
    const { currency } = useAuth();
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState('all');

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/dashboard/stats?range=${range}`);
                setStats(res.data);
            } catch (err) {
                console.error('Error fetching dashboard stats', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [range]);

    if (loading && !stats) return <div className="p-10 text-center text-gray-500">Loading dashboard...</div>;
    // if (!stats) return <div className="p-10 text-center text-red-500">Failed to load stats</div>;

    const ranges = [
        { value: 'all', label: 'All Time' },
        { value: 'this_week', label: 'This Week' },
        { value: 'last_week', label: 'Last Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_year', label: 'Last Year' },
    ];

    return (
        <div className="relative">
            {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-50 z-50 flex items-center justify-center rounded-lg">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
                    <p className="text-gray-500 text-sm mt-1">Overview of your business performance</p>
                </div>

                <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                    {ranges.map((r) => (
                        <button
                            key={r.value}
                            onClick={() => setRange(r.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${range === r.value
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            {r.label}
                        </button>
                    ))}
                    <div className="h-6 w-[1px] bg-gray-200 mx-1 hidden lg:block"></div>
                    <select
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        className="lg:hidden px-2 py-1.5 text-xs font-medium bg-transparent border-0 focus:ring-0 text-gray-800"
                    >
                        {ranges.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Stats Cards */}
            {(() => {
                const received = stats?.totalPaymentsReceived || 0;
                const projectReceived = stats?.totalProjectReceivedAmount || 0;
                const clientExpenses = stats?.clientExpenses || 0;   // passed to client → revenue
                const internalExpenses = stats?.billedExpenses || 0; // company cost → subtracted
                const unbilled = stats?.unbilledExpenses || 0;
                const unreceived = stats?.totalPaymentsUnreceived || 0;
                // Net Revenue = payments + project income + client expenses − internal costs
                const netRevenue = received + projectReceived + clientExpenses - internalExpenses;
                const collectRate = (received + unreceived) > 0 ? Math.round((received / (received + unreceived)) * 100) : 0;

                const cards = [
                    {
                        label: 'Net Revenue',
                        value: formatCurrency(netRevenue, currency),
                        sub: netRevenue > 0 ? 'Keep growing!' : 'Start billing!',
                        icon: <TrendingUp size={18} />,
                        gradient: 'from-blue-500 to-indigo-600',
                        bg: 'bg-blue-50',
                        text: 'text-blue-600',
                        badge: netRevenue > 0 ? { label: 'Revenue', color: 'bg-blue-100 text-blue-700' } : null,
                        bar: null,
                    },
                    {
                        label: 'Unbilled Expenses',
                        value: formatCurrency(unbilled, currency),
                        sub: unbilled > 0 ? 'Invoice them now!' : 'Nothing pending',
                        icon: <Flame size={18} />,
                        gradient: unbilled > 0 ? 'from-amber-400 to-orange-500' : 'from-emerald-400 to-teal-500',
                        bg: unbilled > 0 ? 'bg-amber-50' : 'bg-emerald-50',
                        text: unbilled > 0 ? 'text-amber-600' : 'text-emerald-600',
                        badge: unbilled > 0 ? { label: 'Pending', color: 'bg-amber-100 text-amber-700' } : { label: 'Clear', color: 'bg-emerald-100 text-emerald-700' },
                        bar: null,
                    },
                    {
                        label: 'Billed Expenses',
                        value: formatCurrency(internalExpenses, currency),
                        sub: 'Already invoiced',
                        icon: <CheckCircle size={18} />,
                        gradient: 'from-violet-500 to-purple-600',
                        bg: 'bg-violet-50',
                        text: 'text-violet-600',
                        badge: { label: 'Invoiced', color: 'bg-violet-100 text-violet-700' },
                        bar: null,
                    },
                    {
                        label: 'Payments Received',
                        value: formatCurrency(received, currency),
                        sub: collectRate >= 70 ? 'Great collection rate!' : 'Chase those invoices!',
                        icon: <Banknote size={18} />,
                        gradient: 'from-emerald-500 to-green-600',
                        bg: 'bg-emerald-50',
                        text: 'text-emerald-600',
                        badge: { label: `${collectRate}% collected`, color: collectRate >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700' },
                        bar: { pct: collectRate, color: 'bg-emerald-400' },
                    },
                    {
                        label: 'Outstanding',
                        value: formatCurrency(unreceived, currency),
                        sub: unreceived > 0 ? 'Follow up now!' : 'All collected!',
                        icon: <Target size={18} />,
                        gradient: unreceived > 0 ? 'from-rose-500 to-red-600' : 'from-emerald-500 to-teal-600',
                        bg: unreceived > 0 ? 'bg-rose-50' : 'bg-emerald-50',
                        text: unreceived > 0 ? 'text-rose-600' : 'text-emerald-600',
                        badge: unreceived > 0 ? { label: 'Unpaid', color: 'bg-rose-100 text-rose-700' } : { label: 'All paid!', color: 'bg-emerald-100 text-emerald-700' },
                        bar: null,
                    },
                ];

                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                        {cards.map((card, i) => (
                            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                                {/* Gradient top bar */}
                                <div className={`h-1 bg-gradient-to-r ${card.gradient}`} />
                                <div className="p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{card.label}</p>
                                        <div className={`w-8 h-8 rounded-lg ${card.bg} ${card.text} flex items-center justify-center`}>
                                            {card.icon}
                                        </div>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-900 mb-1">{card.value}</p>
                                    {card.bar && (
                                        <div className="mb-2">
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full ${card.bar.color} rounded-full transition-all duration-700`} style={{ width: `${card.bar.pct}%` }} />
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs text-gray-400">{card.sub}</p>
                                        {card.badge && (
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full text-center ${card.badge.color}`}>{card.badge.label}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Revenue Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 relative overflow-hidden">
                    {(() => {
                        const data = stats.revenueData || [];
                        const best = data.reduce((max: any, m: any) => m.amount > (max?.amount ?? -Infinity) ? m : max, null);
                        const total = data.reduce((s: number, m: any) => s + m.amount, 0);
                        const last = data[data.length - 1]?.amount ?? 0;
                        const prev = data[data.length - 2]?.amount ?? 0;
                        const growth = prev !== 0 ? ((last - prev) / Math.abs(prev)) * 100 : 0;
                        const isUp = growth >= 0;
                        return (
                            <>
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Net Revenue (Last 12 Months)</h2>
                                        <p className="text-sm text-gray-500 mt-0.5">Total: <span className="font-semibold text-gray-700">{formatCurrency(total, currency)}</span></p>
                                    </div>
                                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${isUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                                        {isUp ? '▲' : '▼'} {Math.abs(growth).toFixed(1)}% vs last month
                                    </div>
                                </div>
                                {best && (
                                    <p className="text-xs text-blue-500 mb-3 font-medium">🏆 Best month: <span className="font-bold">{best.name}</span> — {formatCurrency(best.amount, currency)}</p>
                                )}
                            </>
                        );
                    })()}
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={stats.revenueData}>
                                <defs>
                                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.25} />
                                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 11 }} domain={['auto', 'auto']} />
                                <ReferenceLine y={0} stroke="#E5E7EB" strokeDasharray="4 2" />
                                <Tooltip
                                    content={(props: any) => {
                                        const { active, payload, label } = props;
                                        if (!active || !payload?.length) return null;
                                        const val = payload[0]?.value ?? 0;
                                        return (
                                            <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                                <p className="font-semibold text-gray-700">{label}</p>
                                                <p className={`font-bold ${val >= 0 ? 'text-blue-600' : 'text-red-500'}`}>{formatCurrency(val, currency)}</p>
                                            </div>
                                        );
                                    }}
                                />
                                <Area type="monotone" dataKey="amount" stroke="#3B82F6" strokeWidth={2.5} fill="url(#revenueGradient)" dot={(props: any) => {
                                    const { cx, cy, payload } = props;
                                    const data2 = stats.revenueData || [];
                                    const best = data2.reduce((max: any, m: any) => m.amount > (max?.amount ?? -Infinity) ? m : max, null);
                                    if (payload.amount === 0) return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={0} fill="none" />;
                                    if (best && payload.name === best.name) {
                                        return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={6} fill="#3B82F6" stroke="#fff" strokeWidth={2} />;
                                    }
                                    return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={3.5} fill="#3B82F6" stroke="#fff" strokeWidth={1.5} />;
                                }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                {/* Upcoming Payments */}
                <UpcomingPayments />
                {/* Top Expenses */}
                {(() => {
                    const expenses: any[] = stats.topExpenses || [];
                    const total = expenses.reduce((s: number, e: any) => s + e.value, 0);
                    const top = expenses[0];
                    const expColors = ['#f43f5e', '#f97316', '#eab308', '#8b5cf6', '#06b6d4'];
                    const msg = total === 0 ? 'No expenses yet — great!' : top?.value / total > 0.6 ? `${top?.name} is eating most of your budget` : 'Expenses are well distributed';

                    return (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Header with gradient */}
                            <div className="bg-gradient-to-r from-rose-500 to-orange-500 px-5 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Receipt size={15} className="text-white/90" />
                                    <span className="text-white text-xs font-semibold">{msg}</span>
                                </div>
                                {total > 0 && <span className="text-white/90 text-xs font-bold">{formatCurrency(total, currency)} total</span>}
                            </div>

                            <div className="p-5">
                                <h2 className="text-base font-semibold text-gray-800 mb-4">Top Expenses</h2>

                                {expenses.length === 0 ? (
                                    <div className="text-center py-8 text-gray-400">
                                        <CheckCircle size={36} className="mx-auto text-emerald-300 mb-2" />
                                        <p className="text-sm font-medium text-gray-500">No expenses recorded</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {expenses.map((exp: any, i: number) => {
                                            const pct = total > 0 ? (exp.value / total) * 100 : 0;
                                            const color = expColors[i % expColors.length];
                                            return (
                                                <div key={exp.name}>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                                            <span className="text-sm text-gray-700 font-medium">{exp.name}</span>
                                                            {i === 0 && <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600">Top</span>}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-400">{pct.toFixed(0)}%</span>
                                                            <span className="text-sm font-bold text-gray-800">{formatCurrency(exp.value, currency)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-700"
                                                            style={{ width: `${pct}%`, backgroundColor: color }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Top Customers */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    {(() => {
                        const cs = stats.customerStats || [];
                        const total = cs.reduce((s: number, c: any) => s + c.value, 0);
                        const top = cs[0];
                        return (
                            <>
                                <div className="flex items-start justify-between mb-1">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Customers Total Amount</h2>
                                        <p className="text-sm text-gray-500">Total: <span className="font-semibold text-gray-700">{formatCurrency(total, currency)}</span></p>
                                    </div>
                                    {top && (
                                        <div className="text-right">
                                            <p className="text-xs text-gray-400">Top client</p>
                                            <p className="text-sm font-bold text-blue-600">{top.name}</p>
                                            <p className="text-xs text-gray-500">{formatCurrency(top.value, currency)} · {((top.value / total) * 100).toFixed(0)}%</p>
                                        </div>
                                    )}
                                </div>
                                {top && (
                                    <p className="text-xs text-yellow-600 font-medium mb-3">🏆 {top.name} is your best client — keep delivering!</p>
                                )}
                                <div className="flex items-center gap-6">
                                    {/* Donut */}
                                    <div className="shrink-0" style={{ width: 160, height: 160 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={cs}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={72}
                                                    paddingAngle={3}
                                                    labelLine={false}
                                                    label={false}
                                                    dataKey="value"
                                                >
                                                    {cs.map((entry: any, index: number) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={(props: any) => {
                                                    const { active, payload } = props;
                                                    if (!active || !payload?.length) return null;
                                                    const d = payload[0];
                                                    return (
                                                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                                                            <p className="font-semibold text-gray-700">{d.name}</p>
                                                            <p className="text-blue-600 font-bold">{formatCurrency(d.value, currency)}</p>
                                                            <p className="text-gray-400 text-xs">{total ? ((d.value / total) * 100).toFixed(1) : 0}% of total</p>
                                                        </div>
                                                    );
                                                }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    {/* Legend list */}
                                    <div className="flex-1 space-y-2 overflow-hidden">
                                        {cs.map((entry: any, index: number) => {
                                            const pct = total ? ((entry.value / total) * 100) : 0;
                                            return (
                                                <div key={entry.name} className="flex items-center gap-2">
                                                    <span className="shrink-0 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                                    <span className="text-xs text-gray-600 truncate flex-1 min-w-0">{entry.name}</span>
                                                    <span className="text-xs font-semibold text-gray-700 shrink-0">{formatCurrency(entry.value, currency)}</span>
                                                    <span className="text-xs text-gray-400 shrink-0 w-9 text-right">{pct.toFixed(0)}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

            </div>

        </div>
    );
};

export default Dashboard;
